import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, User, LoginCredentials } from '../../api/authApi';
import config from '../../config/config';

// Fallback значения на случай, если config не загрузился
const FALLBACK_CONFIG = {
  app: {
    loginRoute: '/login'
  }
};

// Используем config или fallback
const currentConfig = config || FALLBACK_CONFIG;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithTokens: (accessToken: string, refreshToken: string, user: User) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Проверяем аутентификацию при загрузке приложения
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedUser = authApi.getStoredUser();
        if (storedUser && authApi.isAuthenticated()) {
          // Проверяем, что токен все еще действителен
          const currentUser = await authApi.getCurrentUser();
          if (currentUser) {
            setUser(currentUser);
            setIsAuthenticated(true);
          } else {
            // Токен недействителен, очищаем состояние
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        console.error('Ошибка проверки аутентификации:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true);
      const response = await authApi.login(credentials);
      setUser(response.user);
      setIsAuthenticated(true);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithTokens = (accessToken: string, refreshToken: string, user: User) => {
    const cfg = config?.auth ?? { tokenKey: 'authToken', refreshTokenKey: 'refreshToken', userKey: 'user' };
    localStorage.setItem(cfg.tokenKey, accessToken);
    localStorage.setItem(cfg.refreshTokenKey, refreshToken);
    localStorage.setItem(cfg.userKey, JSON.stringify(user));
    setUser(user);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await authApi.logout();
      setUser(null);
      setIsAuthenticated(false);
      
      // Перенаправляем на страницу входа после выхода
      window.location.href = currentConfig.app.loginRoute;
    } catch (error) {
      console.error('Ошибка выхода:', error);
      // Даже если запрос не удался, очищаем локальное состояние
      setUser(null);
      setIsAuthenticated(false);
      
      // Перенаправляем на страницу входа
      window.location.href = currentConfig.app.loginRoute;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const currentUser = await authApi.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Ошибка обновления данных пользователя:', error);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    loginWithTokens,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};
